import uniq from 'lodash/uniq'
import { createReducer } from 'redux-create-reducer'
import {
  REPORT_PLAYLIST_COLUMN_CUSTOM_FIELD_TOGGLE,
  REPORT_PLAYLIST_COLUMN_TOGGLE,
  REPORT_PLAYLIST_FILTER_TOGGLE,
  REPORT_PLAYLIST_GENERATE,
  REPORT_PLAYLIST_GENERATE_ERROR,
  REPORT_PLAYLIST_GENERATE_SUCCESS,
  REPORT_PLAYLIST_PARAMS_RESET,
  REPORT_PLAYLIST_PARAMS_UPDATE,
  REPORT_PLAYLIST_SECTION_TOGGLE,
} from '@disco/constants/ActionTypes'
type OptionFromSelector = {
  value: number
  label: string
}
type ReportColumnName = 'track_name' | 'album' | 'artist' | string
export type PlaylistReportState = {
  visibleColumns: Array<ReportColumnName>
  customFieldsColumns: string[]
  generatedForCurrentParams: boolean
  sectionExpanded: {
    advancedMetadata: boolean
    filters: boolean
    moreOptions: boolean
    trackInfo: boolean
  }
  form: {
    startDate: Date
    endDate: Date
    selectedChannels: Array<OptionFromSelector>
    selectedTerritories: Array<OptionFromSelector>
    selectedTags: Array<OptionFromSelector>
    generalMetadataValue: string
    advancedMetadataKey: string
    // types come from server
    advancedMetadataValue: string
    writer: string | null | undefined
    sharedWith: string | null | undefined
    owner: string | null | undefined
    playlistTitle: string
    isClientOnly: boolean
    force_all_results_secret_backdoor: boolean
  }
  report: {
    results:
      | null
      | {
          text: 'REPORT_GENERATING' | 'REPORT_DOWNLOADING'
          downloadUrl?: string
          rowCount: number | null | undefined
        }
      | {
          data: {
            table_structure: [string, string][]
            rows: {
              beginning: boolean
              cols: [string, string][]
            }[]
          }
        }
    generationError: any
    generated: boolean
    generating: boolean
  }
}

function matchingColumnsForFilter(filter: string): Array<string> {
  switch (filter) {
    case 'sharedWith':
      return ['shared_with']

    case 'writer':
      return ['writer_name', 'writer_split', 'writer_publisher', 'writer_pro']

    case 'owner':
      return ['creator']

    case 'selectedTags':
      return ['tags']

    default:
      return []
  }
}

function matchingColumnsForAdvancedFilter(filter: string): Array<string> {
  switch (filter) {
    case 'Title':
      return ['title']

    case 'Album':
      return ['album']

    case 'Artist':
      return ['artist']

    case 'Comment':
      return ['comment']

    case 'Composer':
      return ['composer']

    case 'Genre':
      return ['genre']

    case 'Grouping':
      return ['grouping']

    case 'Year':
      return ['year']

    case 'Lyrics':
      return []

    case 'disco_internal_comment':
      return ['internal_comment']

    case 'disco_client_comment':
      return ['client_comment']

    default:
      return []
  }
}

const initialState: PlaylistReportState = {
  visibleColumns: ['track_name', 'album', 'artist'],
  customFieldsColumns: [],
  generatedForCurrentParams: false,
  sectionExpanded: {
    advancedMetadata: false,
    filters: false,
    moreOptions: false,
    trackInfo: false,
  },
  form: {
    startDate: new Date(0),
    endDate: new Date(),
    selectedChannels: [],
    selectedTerritories: [],
    selectedTags: [],
    generalMetadataValue: '',
    advancedMetadataKey: 'Artist',
    advancedMetadataValue: '',
    writer: null,
    sharedWith: null,
    owner: null,
    playlistTitle: '',
    isClientOnly: false,
    force_all_results_secret_backdoor: false,
  },
  report: {
    results: null,
    generationError: null,
    generated: false,
    generating: false,
  },
}
export const playlistReportReducer = createReducer<PlaylistReportState, any>(
  initialState,
  {
    [REPORT_PLAYLIST_PARAMS_RESET]() {
      return {
        ...initialState,
        generatedForCurrentParams: false,
      }
    },

    [REPORT_PLAYLIST_PARAMS_UPDATE](state, action) {
      let visibleColumns = state.visibleColumns
      // action.fields.length is mostly 1-2
      Object.keys(action.fields).map(key => {
        let add: string[] = []
        let remove: string[] = []

        if (key === 'advancedMetadataKey') {
          add = matchingColumnsForAdvancedFilter(action.fields[key])
          remove = matchingColumnsForAdvancedFilter(
            state.form.advancedMetadataKey,
          ).filter(x => !['artist', 'album', 'title'].includes(x))
        } else {
          add = matchingColumnsForFilter(key)
        }

        visibleColumns = uniq(
          visibleColumns.concat(add).filter(c => !remove.includes(c)),
        )
      })
      return {
        ...state,
        generatedForCurrentParams: false,
        visibleColumns,
        form: { ...state.form, ...action.fields },
      }
    },

    [REPORT_PLAYLIST_FILTER_TOGGLE](state, action) {
      // filter is enabled, no state change is required
      if (action.value) return state
      // filter is disabled, reset field
      const remove = matchingColumnsForFilter(action.key)
      const visibleColumns = state.visibleColumns.filter(
        c => !remove.includes(c),
      )
      return {
        ...state,
        visibleColumns,
        form: { ...state.form, [action.key]: initialState.form[action.key] },
      }
    },

    [REPORT_PLAYLIST_SECTION_TOGGLE](state, action) {
      return {
        ...state,
        sectionExpanded: {
          ...state.sectionExpanded,
          [action.key]: action.value,
        },
      }
    },

    [REPORT_PLAYLIST_COLUMN_TOGGLE](state, action) {
      let { visibleColumns } = state
      const { key } = action

      if (visibleColumns.includes(key)) {
        visibleColumns = visibleColumns.filter(column => column !== key)
      } else {
        visibleColumns = visibleColumns.concat(key)
      }

      return { ...state, generatedForCurrentParams: false, visibleColumns }
    },

    [REPORT_PLAYLIST_GENERATE](state) {
      return {
        ...state,
        report: {
          results: null,
          generationError: null,
          generated: false,
          generating: true,
        },
      }
    },

    [REPORT_PLAYLIST_GENERATE_ERROR](state, action) {
      return {
        ...state,
        report: {
          results: null,
          generationError: action.error,
          generated: false,
          generating: false,
        },
      }
    },

    [REPORT_PLAYLIST_GENERATE_SUCCESS](state, action) {
      return {
        ...state,
        generatedForCurrentParams: true,
        report: {
          results: action.results,
          generationError: null,
          generated: true,
          generating: false,
        },
      }
    },

    [REPORT_PLAYLIST_COLUMN_CUSTOM_FIELD_TOGGLE](state, action) {
      let { customFieldsColumns } = state
      const { key } = action

      if (customFieldsColumns.includes(key)) {
        customFieldsColumns = customFieldsColumns.filter(
          column => column !== key,
        )
      } else {
        customFieldsColumns = customFieldsColumns.concat(key)
      }

      return { ...state, customFieldsColumns }
    },
  },
)
